/**
 * Checks if the given value is non-empty.
 *
 * - For strings and arrays, it checks if length is greater than 0.
 * - For booleans, it returns the boolean value itself.
 * - For other types, returns false.
 *
 * @param value - The value to check.
 * @returns `true` if the value is considered required, otherwise `false`.
 */
export function required(value: unknown) {
  if (typeof value === "string" || Array.isArray(value)) {
    return value.length > 0;
  } else if (typeof value === "boolean") {
    return value;
  }

  return false;
}

/**
 * Checks if the given value meets the specified minimum length.
 *
 * - For strings and arrays, it checks if their length is greater than or equal to `min`.
 * - For objects (excluding null), it checks if the number of keys is greater than or equal to `min`.
 * - For all other types, it returns `false`.
 *
 * @param value - The value to check. Can be a string, array, or object.
 * @param min - The minimum length or number of keys required.
 * @returns `true` if the value meets the minimum length, otherwise `false`.
 */
export function minLength(value: unknown, min: number) {
  if (typeof value === "string" || Array.isArray(value)) {
    return value.length >= min;
  } else if (typeof value === "object" && value !== null) {
    return Object.keys(value).length >= min;
  }

  return false;
}

/**
 * Checks if the length of the given value does not exceed the specified maximum.
 *
 * - For strings and arrays, compares their `.length` property to `max`.
 * - For objects, compares the number of own enumerable keys to `max`.
 * - Returns `false` for all other types.
 *
 * @param value - The value to check. Can be a string, array, or object.
 * @param max - The maximum allowed length or number of keys.
 * @returns `true` if the value's length or key count is less than or equal to `max`, otherwise `false`.
 */
export function maxLength(value: unknown, max: number) {
  if (typeof value === "string" || Array.isArray(value)) {
    return value.length <= max;
  } else if (typeof value === "object" && value !== null) {
    return Object.keys(value).length <= max;
  }

  return false;
}

/**
 * Tests whether the provided value matches the given regular expression.
 *
 * @param value - The value to be tested. It will be converted to a string before testing.
 * @param regex - The regular expression to test against the value.
 * @returns `true` if the string representation of the value matches the regex, otherwise `false`.
 */
export function regex(value: unknown, regex: RegExp) {
  return regex.test(String(value));
}

/**
 * Checks if the given value is a number greater than or equal to the specified minimum.
 *
 * @param value - The value to validate. It will be coerced to a string and parsed as an integer.
 * @param min - The minimum allowed value (inclusive).
 * @returns `true` if the parsed number is not NaN and is greater than or equal to `min`, otherwise `false`.
 */
export function minValue(value: unknown, min: number) {
  const num = parseInt(String(value), 10);
  return !isNaN(num) && num >= min;
}

/**
 * Checks if the given value is a number less than or equal to the specified maximum.
 *
 * @param value - The value to validate. It will be converted to a string and parsed as an integer.
 * @param max - The maximum allowed value.
 * @returns `true` if the parsed number is less than or equal to `max`, otherwise `false`.
 */
export function maxValue(value: unknown, max: number) {
  const num = parseInt(String(value), 10);
  return !isNaN(num) && num <= max;
}

/**
 * Validates whether the given value is a valid corporate name.
 *
 * The function checks if the value matches a specific pattern:
 * - Allows letters (a-z, A-Z), digits (0-9), periods, commas, hyphens, parentheses, and spaces.
 * - The name must end with at least one valid character (not a space).
 * - If `ignoreLastSpace` is true, trailing spaces are allowed after the last valid character.
 *
 * @param value - The value to validate as a corporate name.
 * @param ignoreLastSpace - If true, allows trailing spaces at the end of the name. Defaults to `false`.
 * @returns `true` if the value is a valid corporate name according to the pattern; otherwise, `false`.
 */
export function isCorporateName(value: unknown, ignoreLastSpace = false) {
  if (ignoreLastSpace) {
    return regex(value, /^[a-zA-Z0-9.,\-() ]*[a-zA-Z0-9.,\-()]+\s*$/);
  }
  return regex(value, /^[a-zA-Z0-9.,\-() ]*[a-zA-Z0-9.,\-()]+$/);
}

/**
 * Checks if the provided value is a valid name consisting of alphabetic characters and spaces.
 *
 * @param value - The value to validate.
 * @param ignoreLastSpace - If true, allows trailing spaces at the end of the name.
 * @returns `true` if the value matches the name pattern, otherwise `false`.
 */
export function isName(value: unknown, ignoreLastSpace = false) {
  if (ignoreLastSpace) {
    return regex(value, /^[a-zA-Z ]*[a-zA-Z]+\s*$/);
  }
  return regex(value, /^[a-zA-Z ]*[a-zA-Z]+$/);
}

/**
 * Checks if the provided value contains only valid description characters.
 *
 * Allows alphanumeric characters, common punctuation marks, symbols, spaces, and newlines.
 *
 * @param value - The value to validate as a description.
 * @returns `true` if valid as description, otherwise `false`.
 */
export function isDescription(value: unknown) {
  return regex(value, /^[a-zA-Z0-9 \-,.+/&_@#()'\n]*$/);
}

/**
 * Checks if the provided value contains only alphanumeric characters and spaces.
 *
 * @param value - The value to validate.
 * @returns `true` if it matches the pattern, otherwise `false`.
 */
export function isAlphaNumericSpace(value: unknown) {
  return regex(value, /^[a-zA-Z0-9 ]*$/);
}

/**
 * Validates whether the value consists of only numeric characters (0-9).
 *
 * @param value - The value to validate.
 * @param ignoreLastSpace - If true, allows trailing spaces.
 * @returns `true` if the value is numeric, otherwise `false`.
 */
export function isNumeric(value: unknown, ignoreLastSpace = false) {
  if (ignoreLastSpace) {
    return regex(value, /^[0-9]+\s*$/);
  }
  return regex(value, /^[0-9]+$/);
}

/**
 * Validates whether the value contains only alphanumeric characters.
 *
 * @param value - The value to validate.
 * @returns `true` if the value is alphanumeric, otherwise `false`.
 */
export function isAlphaNumeric(value: unknown) {
  return regex(value, /^[a-zA-Z0-9]+$/);
}

/**
 * Validates whether the value is a numeric phone number string.
 *
 * @param value - The value to validate as a phone number.
 * @param ignoreLastSpace - If true, allows trailing spaces.
 * @returns `true` if the value is a valid phone number format, otherwise `false`.
 */
export function isPhone(value: unknown, ignoreLastSpace = false) {
  if (ignoreLastSpace) {
    return regex(value, /^[0-9]+\s*$/);
  }
  return regex(value, /^[0-9]+$/);
}

/**
 * Validates whether the given value is a valid email address.
 *
 * @param value - The value to validate as an email address.
 * @param ignoreLastSpace - If true, allows trailing whitespace after the email address.
 * @returns Returns `true` if the value matches the email pattern, otherwise `false`.
 */
export function isEmail(value: unknown, ignoreLastSpace = false) {
  if (ignoreLastSpace) {
    return regex(
      value,
      /^[a-zA-Z0-9_+-]+(?:\.[a-zA-Z0-9_+-]+)*@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*(?:\.[a-zA-Z0-9-]{2,63})\s*$/
    );
  }
  return regex(
    value,
    /^[a-zA-Z0-9_+-]+(?:\.[a-zA-Z0-9_+-]+)*@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*(?:\.[a-zA-Z0-9-]{2,63})$/
  );
}

/**
 * Checks if the provided value is a valid address string.
 *
 * The address is considered valid if it contains only letters (A-Z, a-z), digits (0-9),
 * apostrophes ('), periods (.), hyphens (-), spaces, commas (,), or forward slashes (/).
 *
 * @param value - The value to validate as an address.
 * @returns `true` if the value matches the address pattern, otherwise `false`.
 */
export function isAddress(value: unknown) {
  return regex(value, /^[A-Za-z0-9'.\-\s,/]*$/);
}

/**
 * Checks if the given value is a valid URL.
 *
 * @param value - The value to validate as a URL.
 * @param ignoreLastSpace - If true, allows trailing whitespace at the end of the URL.
 * @returns `true` if the value matches the URL pattern, otherwise `false`.
 *
 * @remarks
 * Uses a regular expression to validate the URL format. The `ignoreLastSpace` parameter
 * modifies the regex to allow for a trailing space at the end of the URL.
 */
export function isUrl(value: unknown, ignoreLastSpace = false) {
  if (ignoreLastSpace) {
    return regex(
      value,
      /^(https?:\/\/)?([\w-])+\.{1}([a-zA-Z]{2,63})([/\w-]*)*\/?\??([^#\n\r]*)?#?([^\n\r]*)\s*$/
    );
  }
  return regex(
    value,
    /^(https?:\/\/)?([\w-])+\.{1}([a-zA-Z]{2,63})([/\w-]*)*\/?\??([^#\n\r]*)?#?([^\n\r]*)$/
  );
}
